<template>
  <div class="studio-main-layout">
    <!-- Top Bar - Only show in create/edit modes -->
    <div class="studio-top-bar" v-if="isCreating || isEditing">
      <div class="studio-top-bar-content">
        <div class="studio-top-left">
          <button
            type="button"
            class="studio-home-btn"
            title="Home"
            aria-label="Studio home"
            @click="handleBackToList"
          >
            <img :src="homeIcon" alt="" width="20" height="20" />
          </button>
        </div>

        <div class="studio-top-center"></div>

        <!-- Right side - Controls -->
        <div class="studio-top-right">
          <div class="studio-top-controls">
            <!-- Access Level Toggle -->
                    <div class="dash-top-right" v-if="isChartCreator">
                      <div class="tab-div dash-access-toggle justify-space-between">
                        <v-btn-toggle v-model="isShared" class="axis-toggle font-overlay" mandatory
                          @update:modelValue="handleAccessToggleChange">
                          <v-btn :value="false" class="switch-btn" outlined>My Plot</v-btn>
                          <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
                        </v-btn-toggle>
                      </div>
                    </div>

            <!-- Action Buttons -->
            <template v-if="isCreating || isEditing">
              <!-- Save Button - Always show in edit/create mode, especially in embedded/chat mode -->
              <v-btn class="studio-edit-btn" flat 
                      :disabled="isEditing ? (canSave === false) : !isFormValid"
                      :loading="loading" @click="handleSavePlot" title="Save Plot">
                      <img :src="saveIcon" alt="save" width="16" height="16" />
                      <span>Save</span>
                    </v-btn>

              <!-- Delete Button -->
              <v-btn v-if="isChartCreator" class="studio-delete-btn" flat @click="handleDeletePlot"
                      title="Delete Plot">
                      <img :src="deleteIcon" alt="delete" width="16" height="16" />
                    </v-btn>
            </template>
            <template v-else>
              <!-- Edit Button -->
              <v-btn v-if="isChartCreator" class="studio-edit-btn" flat @click="handleEditPlot" 
                     :loading="editLoading" :disabled="editLoading" title="Edit Plot">
                <img :src="editIcon" alt="edit" width="16" height="16" />
                Edit
              </v-btn>
            </template>
          </div>
                  </div>
                </div>
              </div>

    <!-- Main Content Area -->
    <div class="studio-content-wrapper">
      <div class="studio-sidebar">
        <template v-if="isCreating || isEditing">
          <StudioDataLeft ref="studioDataLeft" :selectedChartType="selectedChartType" :isEditing="isEditing"
            :editPlotData="editPlotData" :chartTitle="plotName" :embedded="embedded" @chart-type-changed="handleChartTypeChange" @loading-state="handleLoading"
            @chart-saved="handleChartSaved" @chart-title="handleChartTitle" @x-axis-selected="handleXAxisSelected"
            @y-axis-selected="handleYAxisSelected" @z-axis-selected="handleZAxisSelected"
            @chart-saved-studio="handleChartSavedFromStudio" @update-x-bins="updateXBins" @update-y-bins="updateYBins"
            @grouped-x-axis-data="handleXColorGroupSelected" @grouped-y-axis-data="handleYColorGroupSelected"
            @grouped-z-axis-data="handleZColorGroupSelected" @color-group-key="handleColorGroupKey"
            @color-group-display-name="handleColorGroupDisplayName"
            @sample-error="handleSampleError" @handle-chart-height="handleChartHeight" @x-axis-scale="handleXAxisScale"
            @y-axis-scale="handleYAxisScale" @chart-updated="handleChartUpdated" @edit-cancelled="handleEditCancelled"
            @plot-deleted="handlePlotDeleted" @form-validation="handleFormValidation"
            @update-right-content-style="handleRightContentStyleUpdate" @back-to-list="handleBackToList"
            @access-level-changed="handleAccessLevelChange" @chart-fetched="handleChartFetched" @table-data="handleTableData" />
        </template>
        <template v-else>
          <StudioList ref="studioList" @x-axis-selected="handleXAxisSelected" @y-axis-selected="handleYAxisSelected"
            @z-axis-selected="handleZAxisSelected" @selected-plot="handlePlotSelected" @create-plot="handleCreatePlot" @show-snackbar="handleSnackbar"
            @chart-deleted="handleChartDeleted" @loading-state="handleLoading"
            @grouped-x-axis-data="handleXColorGroupSelected" @grouped-y-axis-data="handleYColorGroupSelected"
            @grouped-z-axis-data="handleZColorGroupSelected" @color-group-key="handleColorGroupKey"
            @color-group-display-name="handleColorGroupDisplayName" @table-data="handleTableData" />
        </template>
      </div>
      
      <div class="studio-content-area" :class="{ 'filter-open': rightDrawerOpen }" :style="studioRightContentStyle">
        <template v-if="!selectedPlot && !isCreating && !isEditing">
          <div class="studio-empty-state">
            <h2>Select or create a plot to get started</h2>
          </div>
        </template>
        <template v-else>
          <div class="studio-flex-row">
            <div class="studio-chart-area" :class="[chartAreaClasses, { 'has-floating-controls': selectedPlot && !isCreating && !isEditing, 'is-loading': loading }]" :style="studioRightContentStyle">
              <!-- Floating controls for view-only mode -->
              <div class="studio-floating-controls" v-if="selectedPlot && !isCreating && !isEditing">
                <div class="studio-floating-controls-content">
                  <!-- Access Level Toggle -->
                  <div class="dash-top-right" v-if="isChartCreator">
                    <div class="tab-div dash-access-toggle justify-space-between">
                      <v-btn-toggle v-model="isShared" class="axis-toggle font-overlay" mandatory
                        @update:modelValue="handleAccessToggleChange">
                        <v-btn :value="false" class="switch-btn" outlined>My Plot</v-btn>
                        <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>

                  <!-- Edit Button -->
                  <v-btn v-if="isChartCreator" class="studio-edit-btn" flat @click="handleEditPlot" 
                         :loading="editLoading" :disabled="editLoading" title="Edit Plot">
                    <img :src="editIcon" alt="edit" width="16" height="16" />
                    Edit
                  </v-btn>
                </div>
              </div>

              <!-- Show progress bar loader when loading -->
              <div v-if="loading" class="chart-loader">
                <div class="progress-container">
                  <div class="progress-header">
                    <h3>Loading Plot Data</h3>
                    <p>Please wait while we fetch and process your data...</p>
                  </div>
                  <div class="progress-bar-container">
                    <div class="progress-bar">
                      <div class="progress-fill"></div>
                    </div>
                    <!-- <div class="progress-text">Processing...</div> -->
                  </div>
                </div>
              </div>
              <!-- Show chart when not loading -->
              <DynamicChart ref="dynamicChart" v-else v-bind="chartProps" :key="chartRenderKey" />
            </div>
        </div>
      </template>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Dialog -->
  <v-dialog transition="dialog-top-transition" v-model="showDeleteDialog" width="417">
    <div class="cross-btn-div" @click="closeDeleteConfirmation">
      <v-img :src="cross" alt="close"></v-img>
    </div>
    <v-alert title="Delete Plot" :text="confirmDeleteText">
      <template #prepend>
        <div class="fd-delete-modal-icon">
          <img :src="delete_icon" alt="" />
        </div>
      </template>
      <div class="alert-btns-div">
        <v-btn class="cancel-btn" flat @click="closeDeleteConfirmation">Cancel</v-btn>
        <v-btn class="delete-btn" flat @click="confirmDelete" :loading="deleteLoading">
          Delete</v-btn>
      </div>
    </v-alert>
  </v-dialog>

  <!-- Snackbar for notifications -->
  <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout">
    {{ snackbar.message }}
  </v-snackbar>
</template>

<script>
import StudioList from './StudioList.vue';
import StudioDataLeft from './StudioDataLeft.vue';
import DynamicChart from '../Charts/DynamicChart.vue';
import api from "../../../axiosInterceptor";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";
import { eventBus } from "../../../eventBus.js";
import { mapGetters } from "vuex";
import { shouldFormatNumbers } from "../../../utils/numberFormatter";
import {
  buildAxisDisplayNamesFromChart,
  buildYAxisColumnString,
  buildYAxisColumnsArray,
  enrichChartAxisDisplayNames,
  parseYAxisKeysFromChart,
} from "../../../utils/chartAxisUtils";

export default {
  name: 'StudioData',
  components: {
    StudioList,
    StudioDataLeft,
    DynamicChart,
  },
  props: {
    plotId: {
      type: [String, Number],
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  mixins: [screenWidthMixin],
  data() {
    return {
      selectedPlot: null,
      isCreating: false,
      isEditing: false,
      editPlotData: null,
      plotName: '',
      isShared: false,
      selectedChartType: "",
      xAxisData: [],
      yAxisData: [],
      zAxisData: [],
      xColorGroupData: {},
      yColorGroupData: {},
      zColorGroupData: {},
      markerSizes: [],
      xAxisDisplayName: "",
      xAxisCaption: "",
      yAxisCaption: "",
      yAxisDisplayNames: [],
      zAxisDisplayName: "",
      loading: false,
      chartName: "",
      xBins: null,
      yBins: null,
      colorGroupKey: "",
      colorGroupDisplayName: "",
      sampleError: "",
      chartHeight: 500,
      xAxisScale: "",
      yAxisScale: "",
      chartRenderKey: 0, // Add this to force chart re-rendering
      homeIcon: require("@/assets/Svg/Icons/home.svg"),
      editIcon: require("../../../assets/Svg/Dashboard/pencil.svg"),
      deleteIcon: require("../../../assets/Svg/trash.svg"),
      saveIcon: require("../../../assets/Svg/save.svg"),
      isFormValid: false,
      snackbar: {
        visible: false,
        message: '',
        color: '',
        timeout: 5000
      },
      showDeleteDialog: false,
      deleteLoading: false,
      dashboardUsageCheck: false,
      dashboardUsageList: [],
      dashboardUsageCount: 0,
      confirmDeleteText: "",
      cross: require("../../../assets/Images/Buttons/cross.png"),
      delete_icon: require("../../../assets/Svg/trash.svg"),
      isXLog: false,
      isYLog: false,
      accessLevel: 'private', // Add accessLevel property for create mode
      chartUpdateAbortController: null,
      chartUsageAbortController: null,
      tableData: [],
      chartDeleteAbortController: null,
      localStorageKeys: {
        studioSession: 'fluidsdata_studio_session',
        createMode: 'fluidsdata_studio_create_mode',
        editMode: 'fluidsdata_studio_edit_mode',
        formData: 'fluidsdata_studio_form_data'
      },
      autoSaveInterval: null, // Add autoSaveInterval property
      lastSavedSession: null,
      saveSessionDebounceTimer: null,
      isSavingSession: false,
      editLoading: false, // Add editLoading property
      editTimeout: null, // Add editTimeout property for cleanup
      newlyCreatedChart: null,
      shouldAutoSelectNewChart: false
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    wrapperClass() {
      const pathSegments = this.$route.path.split('/').filter(Boolean); // Remove empty segments
      const mainRoute = pathSegments.length > 1 ? pathSegments[1].toLowerCase() : null;
      if (mainRoute === 'dashboards') {
        return 'dashboard-wrapper';
      } else if (mainRoute === 'fluidsdata-studio') {
        return 'studio-page-wrapper';
      } else {
        return '';
      }
    },
    safeChartName() {
      const name = this.chartName;
      if (typeof name === 'string') {
        return name;
      } else if (name && typeof name === 'object' && name.toString) {
        return name.toString();
      } else {
        return String(name || '');
      }
    },
    chartProps() {
      const xAxisColumn = this.getAxisColumnName('x');
      const yAxisColumns = this.getAxisColumnNames('y');
      const zAxisColumn = this.getAxisColumnName('z');
      
      const props = {
        chartHeight: this.chartHeight,
        loading: this.loading,
        chartType: this.selectedChartType,
        xAxisData: this.xAxisData,
        yAxisData: this.yAxisData,
        zAxisData: this.zAxisData,
        markerSizes: this.markerSizes,
        xAxisDisplayName: this.xAxisDisplayName,
        xAxisCaption: this.xAxisCaption,
        yAxisCaption: this.yAxisCaption,
        yAxisDisplayNames: this.yAxisDisplayNames,
        zAxisDisplayName: this.zAxisDisplayName,
        chartName: this.chartName,
        xBins: this.xBins,
        yBins: this.yBins,
        xColorGroupData: this.xColorGroupData,
        yColorGroupData: this.yColorGroupData,
        zColorGroupData: this.zColorGroupData,
        colorGroupKey: this.colorGroupKey,
        colorGroupDisplayName: this.colorGroupDisplayName,
        sampleError: this.sampleError,
        xAxisScale: this.xAxisScale,
        yAxisScale: this.yAxisScale,
        isCreateEditMode: this.isCreating || this.isEditing,
        isFilterOpen: this.rightDrawerOpen,
        tableData: this.tableData,
        xAxisColumn: xAxisColumn,
        yAxisColumns: yAxisColumns,
        zAxisColumn: zAxisColumn,
        xAxisDecimalPrecision: this.getDecimalPrecision('x'),
        yAxisDecimalPrecision: this.getDecimalPrecision('y'),
        zAxisDecimalPrecision: this.getDecimalPrecision('z'),
      };
      
      return props;
    },
    canChangeAccessLevel() {
      if (!this.selectedPlot) return true; // Allow for new charts
      const userEmail = this.$store.getters["tenantId/getUser"]?.username;
      return this.selectedPlot.CreatedBy === userEmail;
    },
    isChartCreator() {
      if (!this.selectedPlot) return true; // Allow for new charts
      const userEmail = this.$store.getters["tenantId/getUser"]?.username;
      return this.selectedPlot.CreatedBy === userEmail;
    },
    canSave() {
      if (this.isEditing) {
        if (this.$refs.studioDataLeft) {
          return this.$refs.studioDataLeft.isUpdateEnabled !== false;
        }
        return true;
      }
      return this.isFormValid;
    },
    currentChartWidth() {
      if (this.studioRightContentStyle && this.studioRightContentStyle.width) {
        return this.studioRightContentStyle.width;
      }
      return '90%';
    },
    studioRightContentStyle() {
      if (!this.selectedPlot && !this.isCreating && !this.isEditing) {
        return { width: '70%' };
      }

      if (this.isCreating || this.isEditing) {
        return { width: this.studioCreateEditWidth };
      }

      if (this.rightDrawerOpen) {
        return { width: 'calc(100% - 310px)' };
      } else {
        return { width: '100%' };
      }
    },
    chartAreaClasses() {
      const classes = {};

      if (this.isCreating || this.isEditing) {
        classes['create-edit'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      } else {
        classes['simple-selection'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      }

      return classes;
    },
    chartTitleBarClasses() {
      const classes = {};

      if (this.isCreating || this.isEditing) {
        classes['create-edit'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      } else {
        classes['simple-selection'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      }

      return classes;
    },

    sessionStatus() {
      return {
        hasSession: this.hasValidSession(),
        isCreating: this.isCreating,
        isEditing: this.isEditing,
        lastSaved: this.getLastSavedTime(),
        sessionAge: this.getSessionAge()
      };
    },

    autoSelectionStatus() {
      return {
        shouldAutoSelect: this.shouldAutoSelectNewChart,
        hasNewlyCreatedChart: !!this.newlyCreatedChart,
        newlyCreatedChartId: this.newlyCreatedChart?.ID,
        newlyCreatedChartName: this.newlyCreatedChart?.Name
      };
    }
  },
  watch: {
    selectedPlot: {
      handler(newVal) {
        if (newVal) {
          this.plotName = String(newVal.Name || '');
          this.chartName = String(newVal.Name || '');
          this.isShared = newVal.AccessLevel === 'public';
          this.selectedChartType = newVal.ChartType || '';
          this.xBins = newVal.NumXBins || 0;
          this.yBins = newVal.NumYBins || 0;
          this.xAxisScale = newVal.XAxisScale || 'linear';
          this.yAxisScale = newVal.YAxisScale || 'linear';
          this.isXLog = this.xAxisScale === 'log';
          this.isYLog = this.yAxisScale === 'log';
          
          localStorage.setItem('savedChartData', JSON.stringify(newVal));
        }
      },
      immediate: true,
    },
    isCreating(newVal) {
      if (newVal) {
        this.plotName = '';
        this.isShared = false;
        this.isEditing = false;
        this.editPlotData = null;
        this.resetChartData();
      }
    },
    xAxisData: {
      handler() {
        this.chartRenderKey++;
      }
    },
    yAxisData: {
      handler() {
        this.chartRenderKey++;
      }
    },
    zAxisData: {
      handler() {
        this.chartRenderKey++;
      }
    },
    isEditing(newVal) {
      if (newVal) {
        this.isCreating = false;
      }
    },
    yAxisData: {
      handler() {
        void 0;
      },
      deep: true
    },
    studioRightContentStyle: {
      handler() {
        this.$nextTick(() => {
          this.handleChartResize();
        });
      },
      deep: true
    },
    rightDrawerOpen: {
      handler() {
        this.$nextTick(() => {
          this.handleChartResize();
        });
      }
    },
    '$route': {
      handler(to, from) {
        if (to.path.includes('fluidsdata-studio') && from && !from.path.includes('fluidsdata-studio')) {
          this.$nextTick(() => {
            this.restoreSessionState();
          });
        }
      },
      immediate: false
    },
    plotId: {
      handler(newPlotId) {
        if (newPlotId) {
          this.checkUrlForPlotId();
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.checkUrlForPlotId().then(() => {
      if (!this.plotId && (!this.$route || !this.$route.query || !this.$route.query.plotId)) {
        this.restoreSessionState();
      }
    });

    if (this.isCreating || this.isEditing) {
      this.startAutoSave();
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    eventBus.on("studio-home", this.handleBackToList);
  },
  beforeUnmount() {
    if (this.isCreating || this.isEditing) {
      this.saveSessionState();
    }

    this.stopAutoSave();

    this.cleanupEditOperations();

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    eventBus.off("studio-home", this.handleBackToList);

    this.cancelAllRequests();
  },
  beforeUnmount() {
    if (this.isCreating || this.isEditing) {
      this.saveSessionState();
    }

    this.stopAutoSave();

    this.cleanupEditOperations();

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    eventBus.off("studio-home", this.handleBackToList);

    this.cancelAllRequests();
  },
  methods: {
    cancelChartUpdateRequest() {
      if (this.chartUpdateAbortController) {
        this.chartUpdateAbortController.abort();
        this.chartUpdateAbortController = null;
      }
    },

    cancelChartUsageRequest() {
      if (this.chartUsageAbortController) {
        this.chartUsageAbortController.abort();
        this.chartUpdateAbortController = null;
      }
    },

    cancelChartDeleteRequest() {
      if (this.chartDeleteAbortController) {
        this.chartDeleteAbortController.abort();
        this.chartDeleteAbortController = null;
      }
    },

    shouldFormatAxisData(axisType) {
      if (!this.tableData || this.tableData.length === 0) {
        return false;
      }

      let columnName;
      if (axisType === 'x') {
        if (this.isCreating && this.$refs.studioDataLeft?.selectedXAxisKey) {
          columnName = this.$refs.studioDataLeft.selectedXAxisKey;
        } else {
          columnName = this.selectedPlot?.XAxisColumn;
        }
      } else if (axisType === 'y') {
        if (this.isCreating && this.$refs.studioDataLeft?.selectedYAxisKey && this.$refs.studioDataLeft.selectedYAxisKey.length > 0) {
          columnName = this.$refs.studioDataLeft.selectedYAxisKey[0];
        } else {
          const yColumns = parseYAxisKeysFromChart(this.selectedPlot);
          columnName = yColumns.length > 0 ? yColumns[0] : null;
        }
      } else if (axisType === 'z') {
        if (this.isCreating && this.$refs.studioDataLeft?.selectedZAxisKey) {
          columnName = this.$refs.studioDataLeft.selectedZAxisKey;
        } else {
          columnName = this.selectedPlot?.ZAxisColumn;
        }
      }

      if (!columnName) return false;

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      return shouldFormatNumbers(values);
    },

    shouldAcceptAxisDisplayName(incoming, columnKey, current) {
      if (!incoming) return false;
      if (!columnKey) return true;
      if (incoming !== columnKey) return true;
      return !current || current === columnKey;
    },

    mergeYAxisDisplayNames(incomingNames) {
      const rawKeys = this.getAxisColumnNames('y');
      const nextNames = incomingNames || [];
      if (!this.yAxisDisplayNames?.length) {
        return nextNames;
      }
      return nextNames.map((name, index) => {
        const raw = rawKeys[index];
        const current = this.yAxisDisplayNames[index];
        if (!name) return current || raw || '';
        if (raw && name === raw && current && current !== raw) {
          return current;
        }
        if (name !== raw) return name;
        return current && current !== raw ? current : name;
      });
    },

    getColumnCatalogFromStudioList() {
      const list = this.$refs.studioList;
      if (!list) return null;
      return {
        xAxisItems: list.xAxisItems || [],
        yAxisItems: list.yAxisItems || [],
        zAxisItems: list.zAxisItems || [],
        colorGroupItems: list.colorGroupItems || [],
      };
    },

    async applyChartAxisDisplayNames(chart) {
      if (!chart) return;
      const catalog = this.getColumnCatalogFromStudioList();
      let names;
      if (catalog?.xAxisItems?.length) {
        names = buildAxisDisplayNamesFromChart(chart, catalog);
      } else {
        names = await enrichChartAxisDisplayNames(chart);
      }

      if (names.xAxisDisplayName) {
        this.xAxisDisplayName = names.xAxisDisplayName;
        chart.xAxisDisplayName = names.xAxisDisplayName;
      }
      if (names.yAxisDisplayNames?.length) {
        this.yAxisDisplayNames = [...names.yAxisDisplayNames];
        chart.yAxisDisplayNames = [...names.yAxisDisplayNames];
      }
      if (names.zAxisDisplayName) {
        this.zAxisDisplayName = names.zAxisDisplayName;
        chart.zAxisDisplayName = names.zAxisDisplayName;
      }
      if (names.colorGroupDisplayName) {
        this.colorGroupDisplayName = names.colorGroupDisplayName;
        chart.colorGroupDisplayName = names.colorGroupDisplayName;
        chart.GroupColumnDisplayName = names.colorGroupDisplayName;
      }

      if (this.selectedPlot) {
        Object.assign(this.selectedPlot, {
          xAxisDisplayName: this.xAxisDisplayName,
          yAxisDisplayNames: this.yAxisDisplayNames,
          zAxisDisplayName: this.zAxisDisplayName,
          colorGroupDisplayName: this.colorGroupDisplayName,
          GroupColumnDisplayName: this.colorGroupDisplayName,
        });
      }

      this.chartRenderKey++;
    },

    getAxisColumnName(axisType) {
      if (axisType === 'x') {
        if ((this.isCreating || this.isEditing) && this.$refs.studioDataLeft?.selectedXAxisKey) {
          return this.$refs.studioDataLeft.selectedXAxisKey;
        }
        return this.selectedPlot?.XAxisColumn || '';
      } else if (axisType === 'z') {
        if ((this.isCreating || this.isEditing) && this.$refs.studioDataLeft?.selectedZAxisKey) {
          return this.$refs.studioDataLeft.selectedZAxisKey;
        }
        return this.selectedPlot?.ZAxisColumn || '';
      }
      return '';
    },

    getAxisColumnNames(axisType) {
      if (axisType === 'y') {
        if ((this.isCreating || this.isEditing) && this.$refs.studioDataLeft?.selectedYAxisKey?.length) {
          return this.$refs.studioDataLeft.selectedYAxisKey;
        }
        return parseYAxisKeysFromChart(this.selectedPlot);
      }
      return [];
    },

    getDecimalPrecision(axisType) {
      let columnName;
      if (axisType === 'x') {
        columnName = this.getAxisColumnName('x');
      } else if (axisType === 'y') {
        const yColumns = this.getAxisColumnNames('y');
        columnName = yColumns && yColumns.length > 0 ? yColumns[0] : null;
      } else if (axisType === 'z') {
        columnName = this.getAxisColumnName('z');
      }

      if (!columnName || !this.tableData || this.tableData.length === 0) {
        return 2; // Default precision
      }

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      const shouldFormat = shouldFormatNumbers(values);
      
      return shouldFormat ? 0 : 2;
    },

    cancelAllRequests() {
      this.cancelChartUpdateRequest();
      this.cancelChartUsageRequest();
      this.cancelChartDeleteRequest();
    },

    saveSessionState() {
      try {
        if (this.isSavingSession) {
          return;
        }

        const currentSessionData = {
          timestamp: Date.now(),
          isCreating: this.isCreating,
          isEditing: this.isEditing,
          selectedChartType: this.selectedChartType,
          chartName: this.chartName,
          plotName: this.plotName,
          isShared: this.isShared,
          accessLevel: this.accessLevel,
          xAxisScale: this.xAxisScale,
          yAxisScale: this.yAxisScale,
          isXLog: this.isXLog,
          isYLog: this.isYLog,
          xBins: this.xBins,
          yBins: this.yBins,
          colorGroupKey: this.colorGroupKey,
          colorGroupDisplayName: this.colorGroupDisplayName,
          chartHeight: this.chartHeight,
          sampleError: this.sampleError
        };

        const currentFormData = {
          xAxisData: this.xAxisData,
          yAxisData: this.yAxisData,
          zAxisData: this.zAxisData,
          xColorGroupData: this.xColorGroupData,
          yColorGroupData: this.yColorGroupData,
          zColorGroupData: this.zColorGroupData,
          markerSizes: this.markerSizes,
          xAxisDisplayName: this.xAxisDisplayName,
          xAxisCaption: this.xAxisCaption,
          yAxisCaption: this.yAxisCaption,
          yAxisDisplayNames: this.yAxisDisplayNames,
          zAxisDisplayName: this.zAxisDisplayName
        };

        const sessionChanged = !this.lastSavedSession || 
          JSON.stringify(currentSessionData) !== JSON.stringify(this.lastSavedSession.sessionData) ||
          JSON.stringify(currentFormData) !== JSON.stringify(this.lastSavedSession.formData);

        if (!sessionChanged) {
          return; // No changes, don't save
        }

        this.isSavingSession = true;

        localStorage.setItem(this.localStorageKeys.studioSession, JSON.stringify(currentSessionData));

        localStorage.setItem(this.localStorageKeys.formData, JSON.stringify(currentFormData));

        if (this.isCreating) {
          localStorage.setItem(this.localStorageKeys.createMode, 'true');
        } else if (this.isEditing) {
          localStorage.setItem(this.localStorageKeys.editMode, 'true');
          
          if (this.selectedPlot) {
            this.updateSelectedPlotWithCurrentData();
            localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
          }
        }

        this.lastSavedSession = {
          sessionData: currentSessionData,
          formData: currentFormData,
          timestamp: Date.now()
        };

      } catch (error) {
        console.error('Error saving session state:', error);
      } finally {
        this.isSavingSession = false;
      }
    },

    updateSelectedPlotWithCurrentData() {
      if (!this.selectedPlot) return;

      this.selectedPlot.Name = this.chartName || this.plotName;
      this.selectedPlot.ChartType = this.selectedChartType;
      this.selectedPlot.NumXBins = this.xBins;
      this.selectedPlot.NumYBins = this.yBins;
      this.selectedPlot.XAxisScale = this.xAxisScale;
      this.selectedPlot.YAxisScale = this.yAxisScale;
      this.selectedPlot.AccessLevel = this.isShared ? 'public' : 'private';
      this.selectedPlot.GroupColumn = this.colorGroupKey;
      
      this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
      this.selectedPlot.yAxisDisplayNames = this.yAxisDisplayNames;
      this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
      
      this.selectedPlot.xAxisData = this.xAxisData;
      this.selectedPlot.yAxisData = this.yAxisData;
      this.selectedPlot.zAxisData = this.zAxisData;
      
      this.selectedPlot.xColorGroupData = this.xColorGroupData;
      this.selectedPlot.yColorGroupData = this.yColorGroupData;
      this.selectedPlot.zColorGroupData = this.zColorGroupData;
      
      this.selectedPlot.markerSizes = this.markerSizes;
    },

    async checkUrlForPlotId() {
      let plotId = this.plotId;
      
      if (!plotId && this.$route && this.$route.query) {
        plotId = this.$route.query.plotId;
      }
      
      if (plotId) {
        try {
          const response = await api.get(`public/api/charts/${plotId}`);
          
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            const chartData = response.data.data.chart;
            
            localStorage.setItem('savedChartData', JSON.stringify(chartData));
            
            this.loadChartFromData(chartData);
            
            if (!this.embedded && this.$router) {
              this.$router.replace({ query: {} });
            }
            
            return true;
          } else {
            console.warn('Failed to fetch chart from plotId:', plotId);
            return false;
          }
        } catch (error) {
          console.error('Error fetching chart from plotId:', error);
          return false;
        }
      }
      
      return false;
    },
    async loadChartFromData(chartData) {
      if (chartData && chartData.ID && chartData.ChartType) {
        this.handleChartFetched(chartData);
        
        this.selectedPlot = chartData;
        this.isCreating = false;
        this.isEditing = false;
        
        this.selectedChartType = chartData.ChartType;
        this.chartName = String(chartData.Name || '');
        this.plotName = String(chartData.Name || '');
        this.xBins = chartData.NumXBins;
        this.yBins = chartData.NumYBins;
        this.colorGroupKey = chartData.GroupColumn;
        this.xAxisScale = chartData.XAxisScale || 'linear';
        this.yAxisScale = chartData.YAxisScale || 'linear';
        this.isXLog = this.xAxisScale === 'log';
        this.isYLog = this.yAxisScale === 'log';
        if (chartData.GroupColumn) {
          this.colorGroupKey = chartData.GroupColumn;
        }

        await this.applyChartAxisDisplayNames(chartData);
        
        if (chartData.Filter && chartData.Filter.ID !== undefined) {
          localStorage.setItem("selectedFilterId", chartData.Filter.ID);
        }
        
        this.$nextTick(() => {
          if (this.$refs.studioList) {
            this.$refs.studioList.selectChartById(chartData.ID, false);
          }
        });
      }
    },
    restoreSessionState() {
      if (this.$route.query.plotId) {
        return;
      }
      
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        const formData = localStorage.getItem(this.localStorageKeys.formData);
        const createMode = localStorage.getItem(this.localStorageKeys.createMode);
        const editMode = localStorage.getItem(this.localStorageKeys.editMode);

        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;

          if (sessionAge < 24 * 60 * 60 * 1000) {
            this.selectedChartType = session.selectedChartType || "";
            this.chartName = session.chartName || "";
            this.plotName = session.plotName || "";
            this.isShared = session.isShared || false;
            this.accessLevel = session.accessLevel || 'private';
            this.xAxisScale = session.xAxisScale || "";
            this.yAxisScale = session.yAxisScale || "";
            this.isXLog = session.isXLog || false;
            this.isYLog = session.isYLog || false;
            this.xBins = session.xBins || null;
            this.yBins = session.yBins || null;
            this.colorGroupKey = session.colorGroupKey || "";
            this.colorGroupDisplayName = session.colorGroupDisplayName || "";
            this.chartHeight = session.chartHeight || 500;
            this.sampleError = session.sampleError || "";

            if (formData) {
              const form = JSON.parse(formData);
              this.xAxisData = form.xAxisData || [];
              this.yAxisData = form.yAxisData || [];
              this.zAxisData = form.zAxisData || [];
              this.xColorGroupData = form.xColorGroupData || {};
              this.yColorGroupData = form.yColorGroupData || {};
              this.zColorGroupData = form.zColorGroupData || {};
              this.markerSizes = form.markerSizes || [];
              this.xAxisDisplayName = form.xAxisDisplayName || "";
              this.xAxisCaption = form.xAxisCaption || "";
              this.yAxisCaption = form.yAxisCaption || "";
              this.yAxisDisplayNames = form.yAxisDisplayNames || [];
              this.zAxisDisplayName = form.zAxisDisplayName || "";
            }

            if (createMode === 'true') {
              this.isCreating = true;
              this.isEditing = false;
              this.selectedPlot = null;
            } else if (editMode === 'true') {
              this.isEditing = true;
              this.isCreating = false;
              
              const savedChartData = localStorage.getItem('savedChartData');
              if (savedChartData) {
                try {
                  this.selectedPlot = JSON.parse(savedChartData);
                  
                  if (this.selectedPlot) {
                    if (!this.selectedPlot.xAxisDisplayName && this.xAxisDisplayName) {
                      this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
                    }
                    if (!this.selectedPlot.yAxisDisplayNames && this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
                      this.selectedPlot.yAxisDisplayNames = [...this.yAxisDisplayNames];
                    }
                    if (!this.selectedPlot.zAxisDisplayName && this.zAxisDisplayName) {
                      this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
                    }
                    
                    if (!this.selectedPlot.xAxisData && this.xAxisData && this.xAxisData.length > 0) {
                      this.selectedPlot.xAxisData = [...this.xAxisData];
                    }
                    if (!this.selectedPlot.yAxisData && this.yAxisData && this.yAxisData.length > 0) {
                      this.selectedPlot.yAxisData = [...this.yAxisData];
                    }
                    if (!this.selectedPlot.zAxisData && this.zAxisData && this.zAxisData.length > 0) {
                      this.selectedPlot.zAxisData = [...this.zAxisData];
                    }
                    
                    if (!this.selectedPlot.xColorGroupData && this.xColorGroupData) {
                      this.selectedPlot.xColorGroupData = { ...this.xColorGroupData };
                    }
                    if (!this.selectedPlot.yColorGroupData && this.yColorGroupData) {
                      this.selectedPlot.yColorGroupData = { ...this.yColorGroupData };
                    }
                    if (!this.selectedPlot.zColorGroupData && this.zColorGroupData) {
                      this.selectedPlot.zColorGroupData = { ...this.zColorGroupData };
                    }
                    
                    if (!this.selectedPlot.markerSizes && this.markerSizes && this.markerSizes.length > 0) {
                      this.selectedPlot.markerSizes = [...this.markerSizes];
                    }
                  }
                } catch (error) {
                  console.error('Error parsing savedChartData:', error);
                  this.selectedPlot = null;
                }
              } else {
                console.warn('No savedChartData found for edit mode');
                this.selectedPlot = null;
              }
            } else {
              const savedChartData = localStorage.getItem('savedChartData');
              if (savedChartData) {
                try {
                  const plotData = JSON.parse(savedChartData);
                  if (plotData && plotData.ID && plotData.ChartType) {
                    this.selectedPlot = plotData;
                    this.isCreating = false;
                    this.isEditing = false;
                    
                    this.selectedChartType = plotData.ChartType;
                    this.chartName = String(plotData.Name || '');
                    this.plotName = String(plotData.Name || '');
                    this.xBins = plotData.NumXBins;
                    this.yBins = plotData.NumYBins;
                    this.colorGroupKey = plotData.GroupColumn;
                    this.xAxisScale = plotData.XAxisScale || 'linear';
                    this.yAxisScale = plotData.YAxisScale || 'linear';
                    this.isXLog = this.xAxisScale === 'log';
                    this.isYLog = this.yAxisScale === 'log';
                    this.xAxisDisplayName = plotData.xAxisDisplayName || plotData.XAxisColumn || "";
                    this.zAxisDisplayName = plotData.zAxisDisplayName || plotData.ZAxisColumn || "";
                    
                    if (plotData.yAxisDisplayNames && plotData.yAxisDisplayNames.length > 0) {
                      this.yAxisDisplayNames = [...plotData.yAxisDisplayNames];
                    } else {
                      let yAxisData = plotData.YAxisColumn || plotData.YAxisColumns;
                      if (yAxisData) {
                        if (plotData.processedYAxisDisplayNames) {
                          this.yAxisDisplayNames = plotData.processedYAxisDisplayNames;
                        } else {
                          if (Array.isArray(yAxisData)) {
                            this.yAxisDisplayNames = yAxisData;
                          } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
                            this.yAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
                          } else {
                            this.yAxisDisplayNames = [yAxisData];
                          }
                        }
                      } else {
                        this.yAxisDisplayNames = [];
                      }
                    }
                    
                    if (plotData.GroupColumn) {
                      this.colorGroupKey = plotData.GroupColumn;
                      this.colorGroupDisplayName = plotData.GroupColumnDisplayName || plotData.GroupColumn;
                    }
                  }
                } catch (error) {
                  console.error('Error parsing savedChartData in restoreSessionState:', error);
                  this.selectedPlot = null;
                }
              }
            }

            this.$nextTick(() => {
              if (this.xAxisData && this.xAxisData.length > 0) {
                this.handleXAxisSelected({
                  data: this.xAxisData,
                  displayName: this.xAxisDisplayName,
                  caption: this.xAxisCaption
                });
              }
              
              if (this.yAxisData && this.yAxisData.length > 0) {
                this.handleYAxisSelected({
                  data: this.yAxisData,
                  displayNames: this.yAxisDisplayNames,
                  caption: this.yAxisCaption
                });
              }
              
              if (this.zAxisData && this.zAxisData.length > 0) {
                this.handleZAxisSelected({
                  data: this.zAxisData,
                  displayName: this.zAxisDisplayName,
                  caption: this.zAxisCaption
                });
              }
              
              this.chartRenderKey++;
            });

            return true;
          } else {
            this.clearSessionState();
          }
        }
        return false;
      } catch (error) {
        console.error('Error restoring session state:', error);
        this.clearSessionState();
        return false;
      }
    },

    clearSessionState() {
      try {
        localStorage.removeItem(this.localStorageKeys.studioSession);
        localStorage.removeItem(this.localStorageKeys.createMode);
        localStorage.removeItem(this.localStorageKeys.editMode);
        localStorage.removeItem(this.localStorageKeys.formData);
      } catch (error) {
        console.error('Error clearing session state:', error);
      }
    },

    startAutoSave() {
      this.stopAutoSave();
      
      this.autoSaveInterval = setInterval(() => {
        if (this.isCreating || this.isEditing) {
          this.debouncedSaveSession();
        }
      }, 30000); // Save every 30 seconds
    },

    stopAutoSave() {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
        this.autoSaveInterval = null;
      }
      
      if (this.saveSessionDebounceTimer) {
        clearTimeout(this.saveSessionDebounceTimer);
        this.saveSessionDebounceTimer = null;
      }
    },

    debouncedSaveSession() {
      if (this.saveSessionDebounceTimer) {
        clearTimeout(this.saveSessionDebounceTimer);
      }
      
      this.saveSessionDebounceTimer = setTimeout(() => {
        this.saveSessionState();
        this.saveSessionDebounceTimer = null;
      }, 1000); // 1 second debounce
    },

    resetChartData() {
      this.xAxisData = [];
      this.yAxisData = [];
      this.zAxisData = [];
      this.xColorGroupData = {};
      this.yColorGroupData = {};
      this.zColorGroupData = {};
      this.markerSizes = [];
      this.xAxisDisplayName = "";
      this.xAxisCaption = "";
      this.yAxisCaption = "";
      this.yAxisDisplayNames = [];
      this.zAxisDisplayName = "";
      this.chartName = "";
      this.xBins = null;
      this.yBins = null;
      this.colorGroupKey = "";
      this.colorGroupDisplayName = "";
      this.sampleError = "";
      this.xAxisScale = "";
      this.yAxisScale = "";
    },
    async handlePlotSelected(plot) {
      if (plot) {
        this.selectedPlot = plot;
        this.isCreating = false;
        this.isEditing = false;

        localStorage.setItem('savedChartData', JSON.stringify(plot));

        this.handleRightDrawerStateChange(false);

        this.xAxisScale = plot.XAxisScale || 'linear';
        this.yAxisScale = plot.YAxisScale || 'linear';
        this.isXLog = this.xAxisScale === 'log';
        this.isYLog = this.yAxisScale === 'log';

        this.selectedChartType = plot.ChartType;
        this.chartName = String(plot.Name || '');
        this.plotName = String(plot.Name || '');
        this.xBins = plot.NumXBins;
        this.yBins = plot.NumYBins;
        this.colorGroupKey = plot.GroupColumn;

        this.isShared = plot.AccessLevel === 'public';

        await this.applyChartAxisDisplayNames(plot);
      }
    },
    handleCreatePlot() {
      localStorage.removeItem("savedChartData")
      localStorage.removeItem("newsavedChartData")
      this.selectedPlot = null;
      this.isCreating = true;
      this.isShared = false; // Default to private for new plots
      this.plotName = ""; // Initialize empty plot name
      this.chartName = ""; // Initialize empty chart name
      this.resetChartData();

      this.handleRightDrawerStateChange(false);

      this.startAutoSave();

      this.$nextTick(() => {
        this.debouncedSaveSession();
      });
    },
    handleBackToList() {
      if (!this.shouldAutoSelectNewChart || !this.newlyCreatedChart) {
        localStorage.removeItem("savedChartData")
        localStorage.removeItem("newsavedChartData")
      } else {
        localStorage.removeItem("newsavedChartData")
      }

      this.clearSessionState();

      this.stopAutoSave();

      this.handleRightDrawerStateChange(false);

      if (this.shouldAutoSelectNewChart && this.newlyCreatedChart) {
        
        try {
          let yAxisData = this.newlyCreatedChart.YAxisColumn || this.newlyCreatedChart.YAxisColumns;
          if (yAxisData && !this.newlyCreatedChart.processedYAxisDisplayNames) {
            if (Array.isArray(yAxisData)) {
              this.newlyCreatedChart.processedYAxisDisplayNames = yAxisData;
            } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
              this.newlyCreatedChart.processedYAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
            } else {
              this.newlyCreatedChart.processedYAxisDisplayNames = [yAxisData];
            }
          }
          
          
            this.handlePlotSelected(this.newlyCreatedChart);
              
            this.$nextTick(() => {
              
              if (this.selectedPlot) {
                if (this.selectedPlot.xAxisDisplayName) {
                  this.xAxisDisplayName = this.selectedPlot.xAxisDisplayName;
                }
                if (this.selectedPlot.yAxisDisplayNames && this.selectedPlot.yAxisDisplayNames.length > 0) {
                  this.yAxisDisplayNames = [...this.selectedPlot.yAxisDisplayNames];
                }
                if (this.selectedPlot.zAxisDisplayName) {
                  this.zAxisDisplayName = this.selectedPlot.zAxisDisplayName;
                }
                
                if (this.selectedPlot.ChartType) {
                  this.selectedChartType = this.selectedPlot.ChartType;
                }
                if (this.selectedPlot.Name) {
                  this.chartName = String(this.selectedPlot.Name);
                }
                if (this.selectedPlot.NumXBins) {
                  this.xBins = this.selectedPlot.NumXBins;
                }
                if (this.selectedPlot.NumYBins) {
                  this.yBins = this.selectedPlot.NumYBins;
                }
                if (this.selectedPlot.GroupColumn) {
                  this.colorGroupKey = this.selectedPlot.GroupColumn;
                }
                if (this.selectedPlot.XAxisScale) {
                  this.xAxisScale = this.selectedPlot.XAxisScale;
                  this.isXLog = this.xAxisScale === 'log';
                }
                if (this.selectedPlot.YAxisScale) {
                  this.yAxisScale = this.selectedPlot.YAxisScale;
                  this.isYLog = this.yAxisScale === 'log';
                }
                if (this.selectedPlot.AccessLevel) {
                  this.isShared = this.selectedPlot.AccessLevel === 'public';
                }
                
                if (this.selectedPlot.xAxisData && this.selectedPlot.xAxisData.length > 0) {
                  this.xAxisData = [...this.selectedPlot.xAxisData];
                }
                if (this.selectedPlot.yAxisData && this.selectedPlot.yAxisData.length > 0) {
                  this.yAxisData = [...this.selectedPlot.yAxisData];
                }
                if (this.selectedPlot.zAxisData && this.selectedPlot.zAxisData.length > 0) {
                  this.zAxisData = [...this.selectedPlot.zAxisData];
                }
                
                if (this.selectedPlot.xColorGroupData) {
                  this.xColorGroupData = { ...this.selectedPlot.xColorGroupData };
                }
                if (this.selectedPlot.yColorGroupData) {
                  this.yColorGroupData = { ...this.selectedPlot.yColorGroupData };
                }
                if (this.selectedPlot.zColorGroupData) {
                  this.zColorGroupData = { ...this.selectedPlot.zColorGroupData };
                }
                
                if (this.selectedPlot.plotName) {
                  this.plotName = String(this.selectedPlot.plotName);
                }
                
                this.chartRenderKey++;
              }
            });
            
            this.refreshChartList();
            
            this.shouldAutoSelectNewChart = false;
            this.newlyCreatedChart = null;
            
            return;
        } catch (error) {
          this.shouldAutoSelectNewChart = false;
          this.newlyCreatedChart = null;
        }
      }

      const wasEditing = this.isEditing;
      
      this.isEditing = false;
      this.isCreating = false;
      this.editPlotData = null;
      
      if (!wasEditing) {
        this.selectedPlot = null;
        this.selectedChartType = "";
        this.chartName = "";
        this.plotName = "";
        this.isShared = false;
        this.accessLevel = "private";
        this.isXLog = false;
        this.isYLog = false;

        this.resetChartData();
      } else {
        this.isShared = false;
        this.accessLevel = "private";
        this.isXLog = false;
        this.isYLog = false;
        
        
        if (this.selectedPlot) {
          this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
          this.selectedPlot.yAxisDisplayNames = this.yAxisDisplayNames;
          this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
          
          const plot = this.selectedPlot;
          this.selectedPlot = null;
          this.$nextTick(() => {
            this.selectedPlot = plot;
            
            if (this.xAxisData && this.xAxisData.length > 0) {
              this.handleXAxisSelected({
                data: this.xAxisData,
                displayName: this.xAxisDisplayName,
                caption: this.xAxisCaption
              });
            }
            
            if (this.yAxisData && this.yAxisData.length > 0) {
              this.handleYAxisSelected({
                data: this.yAxisData,
                displayNames: this.yAxisDisplayNames,
                caption: this.yAxisCaption
              });
            }
            
            if (this.zAxisData && this.zAxisData.length > 0) {
              this.handleZAxisSelected({
                data: this.zAxisData,
                displayName: this.zAxisDisplayName
              });
            }
            
            if (this.$refs.studioList && plot.ID) {
              this.$refs.studioList.selectedChartId = plot.ID;
            }
          });
        }
      }

      this.selectedSavedChartName = "";

      this.isFormValid = false;

      this.loading = false;

      if (this.$refs.studioList) {
        this.$refs.studioList.selectedChartId = null;
      }

      this.refreshChartList();

      this.chartRenderKey++;
    },

    handlePlotAccessToggleChange() {
      this.isShared = !!this.isShared;
    },

    safeChartNameTrim() {
      const name = this.chartName;
      if (typeof name === 'string') {
        return name.trim();
      } else if (name && typeof name === 'object' && name.toString) {
        return name.toString().trim();
      } else {
        return String(name || '').trim();
      }
    },

    editPlot() { },

    handleChartHeight(inputs) {
      this.chartHeight = inputs;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleXAxisScale(scale) {
      this.xAxisScale = scale;
      this.isXLog = scale === 'log';
      this.chartRenderKey++; // Force re-render

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYAxisScale(scale) {
      this.yAxisScale = scale;
      this.isYLog = scale === 'log';
      this.chartRenderKey++; // Force re-render

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    updateXBins(newVal) {
      this.xBins = newVal;
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    updateYBins(newVal) {
      this.yBins = newVal;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleChartTitle(chartTitle) {
      this.chartName = String(chartTitle || '');
      this.plotName = String(chartTitle || '');

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleLoading(showloaders) {
      this.loading = showloaders;
    },

    handleChartSaved(chartSaved, requestData) {
      this.chartSaved = chartSaved;
      
      let chartId = null;
      if (requestData && requestData.data && requestData.data.chart && requestData.data.chart.ID) {
        chartId = requestData.data.chart.ID;
      } else if (requestData && requestData.ID) {
        chartId = requestData.ID;
      }
      
      if (this.isEditing) {
        if (this.selectedPlot && this.plotName) {
          this.selectedPlot.Name = this.plotName;
        }
        
        if (this.selectedPlot) {
          localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
        }
        
        this.shouldAutoSelectNewChart = true;
        this.newlyCreatedChart = { ...this.selectedPlot };
        
        this.isEditing = false;
        this.editPlotData = null;
        this.isCreating = false;
        
        this.showSnackbar("Plot updated.", "success");
        
        setTimeout(() => {
          this.handleBackToList();
        }, 1000); // 1 second delay to show success message
      } else {
        this.selectedPlot = null;
        this.isCreating = false;
      }

      this.stopAutoSave();
      this.clearSessionState();

      this.handleRightDrawerStateChange(false);

      if (chartId) {
        this.selectChartById(chartId, this.isEditing);
      }
    },

    handleChartTypeChange(selectedType) {
      this.selectedChartType = selectedType;
    },

    handleXAxisSelected({ data, displayName, caption }) {
      this.xAxisData = data;
      const rawKey = this.getAxisColumnName('x');
      if (this.shouldAcceptAxisDisplayName(displayName, rawKey, this.xAxisDisplayName)) {
        this.xAxisDisplayName = displayName;
      }
      this.xAxisCaption = caption || [];

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYAxisSelected({ data, displayNames, caption }) {
      this.yAxisData = data;
      this.yAxisDisplayNames = this.mergeYAxisDisplayNames(displayNames);
      this.yAxisCaption = caption || [];

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleZAxisSelected({ data, displayName }) {
      
      this.zAxisData = data;
      const rawKey = this.getAxisColumnName('z');
      if (this.shouldAcceptAxisDisplayName(displayName, rawKey, this.zAxisDisplayName)) {
        this.zAxisDisplayName = displayName;
      }
      this.markerSizes = [...data];

      this.$nextTick(() => {
        this.chartRenderKey++;
      });

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleTableData(data) {
      this.tableData = data;
      if (data && Array.isArray(data) && data.length > 0) {
        this.$nextTick(async () => {
          if (this.selectedPlot && !this.isCreating && !this.isEditing) {
            await this.applyChartAxisDisplayNames(this.selectedPlot);
          }
          this.chartRenderKey++;
        });
      }
    },

    handleXColorGroupSelected(groupedXAxisData) {
      this.xColorGroupData = groupedXAxisData;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYColorGroupSelected(groupedYAxisData) {
      this.yColorGroupData = groupedYAxisData;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleZColorGroupSelected(groupedZAxisData) {
      this.zColorGroupData = groupedZAxisData;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleColorGroupKey(selectedColorGroupKey) {
      this.colorGroupKey = selectedColorGroupKey;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleColorGroupDisplayName(selectedColorGroupDisplayName) {
      const rawKey = this.colorGroupKey || this.selectedPlot?.GroupColumn || '';
      if (this.shouldAcceptAxisDisplayName(selectedColorGroupDisplayName, rawKey, this.colorGroupDisplayName)) {
        this.colorGroupDisplayName = selectedColorGroupDisplayName;
      }

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleSavedCharts(title) {
      this.$emit("save-chart", title);
      if (this.isEditing) {
        this.isEditing = false;
        this.editPlotData = null;
        this.selectedPlot = null;
        this.isCreating = false;

        this.handleRightDrawerStateChange(false);
      }
    },

    handleChartSavedFromStudio(chartData) {
      this.selectedChartType = chartData.chartType || this.selectedChartType;
      this.chartName = chartData.chartName || this.chartName;
      this.xBins = chartData.xBins || this.xBins;
      this.yBins = chartData.yBins || this.yBins;
      this.colorGroupKey = chartData.colorGroupKey || this.colorGroupKey;
      this.xAxisScale = chartData.xAxisScale || this.xAxisScale;
      this.yAxisScale = chartData.yAxisScale || this.yAxisScale;
      this.xAxisDisplayName = chartData.xAxisDisplayName || this.xAxisDisplayName;
      this.yAxisDisplayNames = chartData.yAxisDisplayNames || this.yAxisDisplayNames;
      this.zAxisDisplayName = chartData.zAxisDisplayName || this.zAxisDisplayName;
      this.xAxisData = chartData.xAxisData || this.xAxisData;
      this.yAxisData = chartData.yAxisData || this.yAxisData;
      this.zAxisData = chartData.zAxisData || this.zAxisData;
      this.xColorGroupData = chartData.xColorGroupData || this.xColorGroupData;
      this.yColorGroupData = chartData.yColorGroupData || this.yColorGroupData;
      this.zColorGroupData = chartData.zColorGroupData || this.zColorGroupData;
      this.plotName = chartData.plotName || this.plotName;
      this.isShared = typeof chartData.isShared !== 'undefined' ? chartData.isShared : this.isShared;

      if (this.xAxisDisplayName) {
        chartData.xAxisDisplayName = this.xAxisDisplayName;
      }
      if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
        chartData.yAxisDisplayNames = [...this.yAxisDisplayNames];
      }
      if (this.zAxisDisplayName) {
        chartData.zAxisDisplayName = this.zAxisDisplayName;
      }

      this.newlyCreatedChart = chartData;
      this.shouldAutoSelectNewChart = true;
      
      let chartId = null;
      if (chartData && chartData.ID) {
        chartId = chartData.ID;
      }
      
      if (this.isCreating) {
        this.shouldAutoSelectNewChart = true;
      }

      if (this.isEditing && this.selectedPlot) {
        Object.assign(this.selectedPlot, chartData);
        
        localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
      }
      
      if (chartId) {
        this.selectChartById(chartId, this.isEditing);
      }
    },

    async handleChartFetched(completeChartData) {
      const groupColumn = completeChartData.GroupColumn || "";
      const yKeys = parseYAxisKeysFromChart(completeChartData);
      completeChartData.YAxisColumn = buildYAxisColumnString(yKeys, groupColumn);
      completeChartData.YAxisColumns = buildYAxisColumnsArray(yKeys, groupColumn);

      await this.applyChartAxisDisplayNames(completeChartData);
      
      if (this.newlyCreatedChart && this.newlyCreatedChart.ID === completeChartData.ID) {
        this.newlyCreatedChart = completeChartData;
      }
      
        if (this.isCreating && this.shouldAutoSelectNewChart) {
          this.newlyCreatedChart = completeChartData;
          
          if (this.xAxisDisplayName) {
            this.newlyCreatedChart.xAxisDisplayName = this.xAxisDisplayName;
          }
          if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
            this.newlyCreatedChart.yAxisDisplayNames = [...this.yAxisDisplayNames];
          }
          if (this.zAxisDisplayName) {
            this.newlyCreatedChart.zAxisDisplayName = this.zAxisDisplayName;
          }
          
          this.editPlotData = {
            chartType: this.newlyCreatedChart.ChartType || this.selectedChartType,
            chartName: this.newlyCreatedChart.Name || this.chartName,
            xBins: this.newlyCreatedChart.NumXBins || this.xBins,
            yBins: this.newlyCreatedChart.NumYBins || this.yBins,
            colorGroupKey: this.newlyCreatedChart.GroupColumn || this.colorGroupKey,
            xAxisScale: this.newlyCreatedChart.XAxisScale || this.xAxisScale,
            yAxisScale: this.newlyCreatedChart.YAxisScale || this.yAxisScale,
            
            xAxisDisplayName: this.xAxisDisplayName || this.newlyCreatedChart.xAxisDisplayName,
            yAxisDisplayNames: this.yAxisDisplayNames || this.newlyCreatedChart.yAxisDisplayNames,
            zAxisDisplayName: this.zAxisDisplayName || this.newlyCreatedChart.zAxisDisplayName,
            
            xAxisData: this.xAxisData || [],
            yAxisData: this.yAxisData || [],
            zAxisData: this.zAxisData || [],
            
            xColorGroupData: this.xColorGroupData || {},
            yColorGroupData: this.yColorGroupData || {},
            zColorGroupData: this.zColorGroupData || {},
            
            plotName: this.plotName || this.newlyCreatedChart.plotName,
            isShared: this.newlyCreatedChart.AccessLevel === 'public' || this.isShared,
            
            XAxisColumn: this.newlyCreatedChart.XAxisColumn,
            YAxisColumn: this.newlyCreatedChart.YAxisColumn,
            YAxisColumns: this.newlyCreatedChart.YAxisColumns,
            ZAxisColumn: this.newlyCreatedChart.ZAxisColumn,
            TableType: this.newlyCreatedChart.TableType
          };
          
          
          if (this.newlyCreatedChart.ChartType) {
            this.selectedChartType = this.newlyCreatedChart.ChartType;
          }
          if (this.newlyCreatedChart.Name) {
            this.chartName = String(this.newlyCreatedChart.Name);
            this.plotName = String(this.newlyCreatedChart.Name);
          }
          if (this.newlyCreatedChart.NumXBins) {
            this.xBins = this.newlyCreatedChart.NumXBins;
          }
          if (this.newlyCreatedChart.NumYBins) {
            this.yBins = this.newlyCreatedChart.NumYBins;
          }
          if (this.newlyCreatedChart.GroupColumn) {
            this.colorGroupKey = this.newlyCreatedChart.GroupColumn;
          }
          if (this.newlyCreatedChart.XAxisScale) {
            this.xAxisScale = this.newlyCreatedChart.XAxisScale;
            this.isXLog = this.xAxisScale === 'log';
          }
          if (this.newlyCreatedChart.YAxisScale) {
            this.yAxisScale = this.newlyCreatedChart.YAxisScale;
            this.isYLog = this.yAxisScale === 'log';
          }
          if (this.newlyCreatedChart.AccessLevel) {
            this.isShared = this.newlyCreatedChart.AccessLevel === 'public';
          }
          if (this.newlyCreatedChart.plotName) {
            this.plotName = String(this.newlyCreatedChart.plotName);
            this.chartName = String(this.newlyCreatedChart.plotName);
          }
          
          
          
          this.showSnackbar("Plot created successfully!", "success");
          
          this.isCreating = false;
          
          this.stopAutoSave();
          
          this.handleRightDrawerStateChange(false);
          
          setTimeout(() => {
            this.handleBackToList();
          }, 1000); // 1 second delay to show success message
          
        }
      
      if (this.isEditing && this.selectedPlot && this.selectedPlot.ID === completeChartData.ID) {
        this.selectedPlot = completeChartData;
        
        this.newlyCreatedChart = completeChartData;
        
        localStorage.setItem('savedChartData', JSON.stringify(completeChartData));
        
        this.selectedChartType = completeChartData.ChartType;
        this.chartName = String(completeChartData.Name || '');
        this.plotName = String(completeChartData.Name || '');
        this.xBins = completeChartData.NumXBins;
        this.yBins = completeChartData.NumYBins;
        this.colorGroupKey = completeChartData.GroupColumn;
        this.xAxisScale = completeChartData.XAxisScale || 'linear';
        this.yAxisScale = completeChartData.YAxisScale || 'linear';
        this.isXLog = this.xAxisScale === 'log';
        this.isYLog = this.yAxisScale === 'log';
        this.xAxisDisplayName = completeChartData.xAxisDisplayName || completeChartData.XAxisColumn || "";
        this.zAxisDisplayName = completeChartData.zAxisDisplayName || completeChartData.ZAxisColumn || "";
        
        if (completeChartData.yAxisDisplayNames && completeChartData.yAxisDisplayNames.length > 0) {
          this.yAxisDisplayNames = [...completeChartData.yAxisDisplayNames];
        } else {
          let yAxisData = completeChartData.YAxisColumn || completeChartData.YAxisColumns;
          if (yAxisData) {
            if (completeChartData.processedYAxisDisplayNames) {
              this.yAxisDisplayNames = completeChartData.processedYAxisDisplayNames;
            } else {
              if (Array.isArray(yAxisData)) {
                this.yAxisDisplayNames = yAxisData;
              } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
                this.yAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
              } else {
                this.yAxisDisplayNames = [yAxisData];
              }
            }
          } else {
            this.yAxisDisplayNames = [];
          }
        }
        
        if (completeChartData.GroupColumn) {
          this.colorGroupKey = completeChartData.GroupColumn;
          this.colorGroupDisplayName = completeChartData.GroupColumnDisplayName || completeChartData.GroupColumn;
        }
        
        this.isShared = completeChartData.AccessLevel === 'public';
        
        this.$nextTick(() => {
          this.chartRenderKey++;
        });
      }
    },

    handleSampleError(sampleError) {
      this.sampleError = sampleError;

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleFormValidation(isValid) {
      this.isFormValid = isValid;
    },

    handleEditPlot() {
      if (!this.isChartCreator) {
        this.showSnackbar("You can only edit charts that you created.", "error");
        return;
      }

      this.editLoading = true;

      this.editTimeout = setTimeout(() => {
        if (this.editLoading) {
          this.editLoading = false;
          this.showSnackbar("Edit operation timed out. Please try again.", "error");
        }
      }, 10000); // 10 second timeout

      try {
        if (this.selectedPlot && this.selectedPlot.Name) {
          this.plotName = String(this.selectedPlot.Name || '');
          this.chartName = String(this.selectedPlot.Name || '');
        }
        
        const plot = this.selectedPlot || {};
        const groupCol = plot.GroupColumn || this.colorGroupKey || '';
        const yKeys = parseYAxisKeysFromChart(plot);
        const yAxisColumnString = buildYAxisColumnString(yKeys, groupCol);
        const yAxisColumnsArray = buildYAxisColumnsArray(yKeys, groupCol);

        this.editPlotData = {
          ID: plot.ID || null,
          Name: plot.Name || this.chartName || '',
          chartType: this.selectedChartType || plot.ChartType || '',
          chartName: this.chartName || plot.Name || '',
          xBins: this.xBins !== null && this.xBins !== undefined ? this.xBins : (plot.NumXBins || 20),
          yBins: this.yBins !== null && this.yBins !== undefined ? this.yBins : (plot.NumYBins || 20),
          colorGroupKey: groupCol,
          xAxisScale: this.xAxisScale || plot.XAxisScale || 'linear',
          yAxisScale: this.yAxisScale || plot.YAxisScale || 'linear',
          xAxisDisplayName: this.xAxisDisplayName || plot.xAxisDisplayName || plot.XAxisColumn || '',
          yAxisDisplayNames: this.yAxisDisplayNames?.length > 0 ? this.yAxisDisplayNames : (plot.yAxisDisplayNames || []),
          zAxisDisplayName: this.zAxisDisplayName || plot.zAxisDisplayName || plot.ZAxisColumn || '',
          colorGroupDisplayName:
            this.colorGroupDisplayName ||
            plot.GroupColumnDisplayName ||
            plot.colorGroupDisplayName ||
            '',
          tableData: this.tableData?.length > 0 ? this.tableData : [],
          xAxisData: this.xAxisData?.length > 0 ? this.xAxisData : [],
          yAxisData: this.yAxisData?.length > 0 ? this.yAxisData : [],
          zAxisData: this.zAxisData?.length > 0 ? this.zAxisData : [],
          xColorGroupData: this.xColorGroupData && Object.keys(this.xColorGroupData).length > 0 ? this.xColorGroupData : {},
          yColorGroupData: this.yColorGroupData && Object.keys(this.yColorGroupData).length > 0 ? this.yColorGroupData : {},
          zColorGroupData: this.zColorGroupData && Object.keys(this.zColorGroupData).length > 0 ? this.zColorGroupData : {},
          plotName: this.plotName || plot.Name || '',
          isShared: this.isShared !== undefined ? this.isShared : (plot.AccessLevel === 'public'),
          TableType: plot.TableType || '',
          XAxisColumn: plot.XAxisColumn || '',
          YAxisColumn: yAxisColumnString,
          YAxisColumns: yAxisColumnsArray,
          ZAxisColumn: plot.ZAxisColumn || '',
          GroupColumn: groupCol,
          Filter: plot.Filter || {},
        };

        this.sampleError = "";
        this.isEditing = true;
        this.isCreating = false;
        
        this.handleRightDrawerStateChange(false);

        this.startAutoSave();

        this.$nextTick(() => {
          if (this.$refs.studioDataLeft) {
            this.$refs.studioDataLeft.seedEditSessionFromView({
              tableData: this.tableData,
              xAxisData: this.xAxisData,
              yAxisData: this.yAxisData,
              zAxisData: this.zAxisData,
              filter: plot.Filter || {},
              xAxisDisplayName: this.xAxisDisplayName,
              yAxisDisplayNames: this.yAxisDisplayNames,
              zAxisDisplayName: this.zAxisDisplayName,
              colorGroupDisplayName: this.colorGroupDisplayName,
            });
          }
          this.debouncedSaveSession();
        });

        if (this.editTimeout) {
          clearTimeout(this.editTimeout);
          this.editTimeout = null;
        }

      } catch (error) {
        console.error('Error starting edit mode:', error);
        this.handleEditError(error, 'starting edit mode');
      } finally {
        this.editLoading = false;
      }
    },

    handleDeletePlot() {
      if (!this.isChartCreator) {
        this.showSnackbar("You can only delete charts that you created.", "error");
        return;
      }

      if (this.selectedPlot && this.selectedPlot.ID) {
        this.checkDashboardUsage();
      }
    },
    handleEditCancelled() {
      this.isEditing = false;
      this.editPlotData = null;

      this.stopAutoSave();
      this.clearSessionState();

      this.handleRightDrawerStateChange(false);

      if (this.selectedPlot) {
        this.handlePlotSelected(this.selectedPlot);
      }
    },

    handlePlotDeleted() {
      this.selectedPlot = null;
      this.isEditing = false;
      this.editPlotData = null;

      this.stopAutoSave();
      this.clearSessionState();

      this.handleRightDrawerStateChange(false);

      this.refreshChartList(); // Call the new method
    },
    async handleSavePlot() {
      if (this.isEditing && !this.isChartCreator) {
        this.showSnackbar("You can only save charts that you created.", "error");
        return;
      }

      if (this.$refs.studioDataLeft) {
        try {
          if (this.isEditing) {
            await this.$refs.studioDataLeft.updateChart();
            
            this.isEditing = false;
            
            this.stopAutoSave();
            
            this.handleRightDrawerStateChange(false);
            
            
            if (this.selectedPlot && this.plotName) {
              this.selectedPlot.Name = this.plotName;
            }
            
            this.editPlotData = null;
            
            this.$nextTick(() => {
              this.chartRenderKey++;
              
              if (this.$refs.studioList && this.selectedPlot && this.selectedPlot.ID) {
                this.$refs.studioList.selectedChartId = this.selectedPlot.ID;
              }
            });
            
          } else {
            await this.$refs.studioDataLeft.createChart(this.accessLevel);
          }
        } catch (error) {
          console.error('Error saving plot:', error);
          this.showSnackbar("An error occurred while saving the plot.", "error");
        }
      }
    },
    handleChartResize() {
      this.chartRenderKey++; // Increment key to force re-render
    },
    async handleAccessToggleChange(newAccessLevel) {
      if (!this.isChartCreator) {
        this.showSnackbar("You can only modify charts that you created.", "error");
        return;
      }

      let accessLevel = newAccessLevel === true ? "public" : newAccessLevel === false ? "private" : newAccessLevel;
      if (accessLevel !== "public" && accessLevel !== "private") {
        accessLevel = "private";
      }

      if (this.isCreating) {
        this.accessLevel = accessLevel;
        this.isShared = accessLevel === 'public';

        this.$nextTick(() => {
          this.debouncedSaveSession();
        });

        return;
      }

      const selectedPlot = this.selectedPlot
        ? this.selectedPlot
        : this.savedCharts.find(
          (chart) => chart.Name === this.selectedSavedChartName
        );

      if (!selectedPlot || !selectedPlot.ID) {
        this.showSnackbar("Selected plot not found.", "error");
        return;
      }

      const chartId = selectedPlot.ID;

      const {
        Name,
        ChartType,
        TableType,
        XAxisColumn,
        YAxisColumns,
        ZAxisColumn,
        NumXBins,
        NumYBins,
        GroupColumn,
        Filter,
        XAxisScale,
        YAxisScale,
      } = selectedPlot;

      const requestData = {
        data: {
          chart: {
            name: Name,
            ChartType,
            TableType,
            XAxisColumn,
            YAxisColumns,
            ZAxisColumn,
            NumXBins,
            NumYBins,
            GroupColumn,
            Filter,
            XAxisScale,
            YAxisScale,
            AccessLevel: accessLevel,
          }
        }
      };

      try {
        const response = await api.put(
          `public/api/charts/${chartId}`,
          requestData
        );
        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          this.selectedPlot.AccessLevel = accessLevel;
          this.isShared = accessLevel === 'public';
          this.showSnackbar("Plot access level updated successfully.", "success");
          this.chartSaved = true;
          this.$emit("chart-saved", true);
        } else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.showSnackbar(error.response.data.error, "error");
        } else if (error.response && error.response.data && error.response.data.message) {
          this.showSnackbar(error.response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    async updateChartAccessLevel(newAccessLevel) {
      if (!this.selectedPlot) return;

      this.cancelChartUpdateRequest();

      this.chartUpdateAbortController = new AbortController();

      try {
        const requestData = {
          data: {
            chart: {
              AccessLevel: newAccessLevel
            }
          }
        };

        const response = await api.put(`public/api/charts/${this.selectedPlot.ID}`, requestData, {
          signal: this.chartUpdateAbortController.signal
        });

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.selectedPlot.AccessLevel = newAccessLevel;

          this.refreshChartList();

          this.handleSnackbar({
            message: `Plot access level changed to ${newAccessLevel}`,
            color: "success"
          });
        } else {
          throw new Error(response?.data?.message || "Failed to update plot access level");
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error("Error updating chart access level:", error);


        this.handleSnackbar({
          message: error.response?.data?.error || error.message || "An error occurred while updating the plot access level",
          color: "error"
        });
      } finally {
        this.chartUpdateAbortController = null;
      }
    },
    handleRightContentStyleUpdate(newStyle) {
      if (newStyle && newStyle.width) {
        const isDrawerOpen = newStyle.width.includes('310px');
        this.handleRightDrawerStateChange(isDrawerOpen);
      }
      this.$nextTick(() => {
        this.handleChartResize();
      });
    },
    handleSnackbar(messageObj) {
      this.snackbar.message = messageObj.message;
      this.snackbar.color = messageObj.color;
      this.snackbar.timeout = 5000;
      this.snackbar.visible = true;
    },

    refreshChartList() {
      if (this.$refs.studioList) {
        this.$refs.studioList.getSavedCharts();
      }
    },

    handleChartDeleted() {
      this.selectedPlot = null;
      this.isEditing = false;
      this.editPlotData = null;

      this.handleRightDrawerStateChange(false);

      this.refreshChartList();
    },

    handleChartUpdated() {
      if (this.isEditing && this.selectedPlot && this.plotName) {
        this.selectedPlot.Name = this.plotName;
      }

      try {
        const raw =
          localStorage.getItem("newsavedChartData") ||
          localStorage.getItem("savedChartData");
        if (raw) {
          const chartData = JSON.parse(raw);
          if (chartData && chartData.ID) {
            const groupColumn = chartData.GroupColumn || "";
            const yKeys = parseYAxisKeysFromChart(chartData);
            chartData.YAxisColumn = buildYAxisColumnString(yKeys, groupColumn);
            chartData.YAxisColumns = buildYAxisColumnsArray(yKeys, groupColumn);
            this.selectedPlot = { ...(this.selectedPlot || {}), ...chartData };
            this.handleChartFetched(chartData);
          }
        }
      } catch (error) {
        console.error("Error syncing plot after chart update:", error);
      }

      this.chartRenderKey++;
      this.refreshChartList();
    },

    async checkDashboardUsage() {
      this.cancelChartUsageRequest();

      this.chartUsageAbortController = new AbortController();

      try {
        const response = await api.get(`/public/api/charts/${this.selectedPlot.ID}/usage`, {
          signal: this.chartUsageAbortController.signal
        });
        const data = response.data.data;
        this.dashboardUsageCount = data.length;
        this.dashboardUsageList = data;
        
        if (data.length === 0) {
          this.confirmDeleteText = `You are deleting the plot ${this.selectedPlot.Name}. Once deleted, it will be permanently removed and cannot be restored. Continue ?`;
        } else {
          this.confirmDeleteText = `The plot ${this.selectedPlot.Name} is currently used in ${data.length} dashboard${data.length > 1 ? 's' : ''}. Deleting it will remove it from all dashboards and permanently delete it from the system. This action cannot be undone. Continue ?`;
        }
        
        this.showDeleteDialog = true;
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Error checking chart usage:', error);
        this.dashboardUsageCount = 0;
        this.dashboardUsageList = [];
        this.confirmDeleteText = `You are deleting the plot ${this.selectedPlot.Name}. Once deleted, it will be permanently removed and cannot be restored. Continue ?`;
        this.showDeleteDialog = true;
      } finally {
        this.chartUsageAbortController = null;
      }
    },

    async confirmDelete() {
      this.deleteLoading = true;

      this.cancelChartDeleteRequest();

      this.chartDeleteAbortController = new AbortController();

      try {
        const response = await api.delete(`public/api/charts/${this.selectedPlot.ID}`, {
          signal: this.chartDeleteAbortController.signal
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.handleSnackbar({
            message: `"${this.selectedPlot.Name}" deleted.`,
            color: "success"
          });
          this.selectedPlot = null;
          this.isEditing = false;
          this.editPlotData = null;

          this.handleRightDrawerStateChange(false);

          this.refreshChartList();
        } else {
          throw new Error(response?.data?.message || "Failed to delete plot");
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error("Error deleting plot:", error);
        this.handleSnackbar({
          message: error.response?.data?.error || error.message || "An error occurred while deleting the plot",
          color: "error"
        });
      } finally {
        this.deleteLoading = false;
        this.showDeleteDialog = false;
        this.chartDeleteAbortController = null;
      }
    },
    closeDeleteConfirmation() {
      this.showDeleteDialog = false;
      this.dashboardUsageCount = 0;
      this.dashboardUsageList = [];
    },
    handleXScaleToggleChange(value) {
      this.isXLog = value;
      this.xAxisScale = value ? 'log' : 'linear';
      this.$emit('x-axis-scale', this.xAxisScale);
      this.chartRenderKey++; // Force re-render to apply new scale
    },
    handleYScaleToggleChange(value) {
      this.isYLog = value;
      this.yAxisScale = value ? 'log' : 'linear';
      this.$emit('y-axis-scale', this.yAxisScale);
      this.chartRenderKey++; // Force re-render to apply new scale
    },
    handleAccessLevelChange(newAccessLevel) {
      this.isShared = newAccessLevel === 'public';
      this.accessLevel = newAccessLevel;
      this.showSnackbar(`Access level changed to ${newAccessLevel}`, 'success');
      this.chartSaved = true;
      this.$emit('chart-saved', true);

      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleVisibilityChange() {
      if (document.hidden) {
        if (this.isCreating || this.isEditing) {
          this.debouncedSaveSession();
        }
      } else {
        void 0;
      }
    },

    handleBeforeUnload() {
      if (this.isCreating || this.isEditing) {
        this.debouncedSaveSession();
      }
    },

    clearSessionManually() {
      this.clearSessionState();
      this.showSnackbar("Session state cleared", "info");
    },

    triggerBackToList() {
      this.handleBackToList();
    },

    syncDisplayNames() {
      
      if (this.selectedPlot) {
        if (this.selectedPlot.xAxisDisplayName) {
          this.xAxisDisplayName = this.selectedPlot.xAxisDisplayName;
        }
        if (this.selectedPlot.yAxisDisplayNames && this.selectedPlot.yAxisDisplayNames.length > 0) {
          this.yAxisDisplayNames = [...this.selectedPlot.yAxisDisplayNames];
        }
        if (this.selectedPlot.zAxisDisplayName) {
          this.zAxisDisplayName = this.selectedPlot.zAxisDisplayName;
        }
        
        
        this.chartRenderKey++;
      } else {
        void 0;
      }
    },

    checkDisplayNamesState() {
      
      if (this.selectedPlot) {
        void 0;
      } else {
        void 0;
      }
      
    },

    setDisplayNamesFromForm() {
      if (this.xAxisDisplayName && this.yAxisDisplayNames?.length && this.zAxisDisplayName) {
        return;
      }

      if (!this.xAxisDisplayName && this.xAxisData && this.xAxisData.length > 0) {
        const firstRow = this.xAxisData[0];
        if (firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow)) {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.xAxisDisplayName = keys[0];
          }
        }
      }
      
      if ((!this.yAxisDisplayNames || this.yAxisDisplayNames.length === 0) && this.yAxisData && this.yAxisData.length > 0) {
        const firstRow = this.yAxisData[0];
        if (firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow)) {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.yAxisDisplayNames = keys;
          }
        }
      }
      
      if (!this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
        const firstRow = this.zAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.zAxisDisplayName = keys[0];
          }
        }
      }
      
      
      this.chartRenderKey++;
    },

    forceDisplayNameExtraction() {
      
      this.setDisplayNamesFromForm();
      
      this.saveSessionState();
      
    },

    testDisplayNamesFlow() {
      void 0;
    },

    testCreateSaveFlow() {
      this.checkDisplayNamesState();
      this.forceDisplayNameExtraction();
      
      this.checkDisplayNamesState();
      this.handleBackToList();
    },

    triggerChartSave() {
      
      if (this.$refs.studioDataLeft) {
        if (this.isCreating) {
          this.$refs.studioDataLeft.createChart(this.accessLevel);
        } else if (this.isEditing) {
          this.$refs.studioDataLeft.updateChart();
        } else {
          void 0;
        }
      } else {
        void 0;
      }
    },

    selectChartById(chartId, forceRefresh = false) {
      
      this.clearConflictingLocalStorage();
      
      this.$nextTick(() => {
        if (this.$refs.studioList) {
          this.$refs.studioList.selectChartById(chartId, forceRefresh);
        } else {
          setTimeout(() => {
            if (this.$refs.studioList) {
              this.$refs.studioList.selectChartById(chartId, forceRefresh);
            } else {
              void 0;
            }
          }, 500);
        }
      });
    },

    clearConflictingLocalStorage() {
      try {
        localStorage.removeItem("newsavedChartData");
        localStorage.removeItem("initialChartData");
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
    },

    hasValidSession() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;
          return sessionAge < 24 * 60 * 60 * 1000; // 24 hours
        }
        return false;
      } catch (error) {
        return false;
      }
    },

    getLastSavedTime() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          return new Date(session.timestamp).toLocaleString();
        }
        return null;
      } catch (error) {
        return null;
      }
    },

    getSessionAge() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;
          const hours = Math.floor(sessionAge / (1000 * 60 * 60));
          const minutes = Math.floor((sessionAge % (1000 * 60 * 60)) / (1000 * 60));
          return `${hours}h ${minutes}m ago`;
        }
        return null;
      } catch (error) {
        return null;
      }
    },

    cleanupEditOperations() {
      if (this.editLoading) {
        this.editLoading = false;
      }
      
      if (this.editTimeout) {
        clearTimeout(this.editTimeout);
        this.editTimeout = null;
      }
      
      if (this.isEditing) {
        this.isEditing = false;
        this.editPlotData = null;
      }
      
      this.stopAutoSave();
    },

    handleEditError(error, context = 'edit operation') {
      console.error(`Error during ${context}:`, error);
      
      this.isEditing = false;
      this.editPlotData = null;
      this.editLoading = false;
      
      if (this.editTimeout) {
        clearTimeout(this.editTimeout);
        this.editTimeout = null;
      }
      
      this.stopAutoSave();
      
      let errorMessage = "An error occurred during the edit operation. Please try again.";
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.showSnackbar(errorMessage, "error");
      
      this.$emit('edit-error', { error, context });
    }
  }
};
</script>
